// Installing TradePrep to the home screen (the PWA). Like platform.ts this is
// the only place that knows how: Android browsers hand the app an install
// prompt, iPhones have none (the learner uses Share → Add to Home Screen), and
// some in-app browsers can't install at all.

import { useSyncExternalStore } from 'react';
import { isNative, isStandalonePwa } from './platform';

export type InstallContext =
  /** Already running from the home screen, or in the native app: nothing to offer. */
  | 'installed'
  /** Android (or desktop) browser that gave us an install prompt: one tap. */
  | 'prompt'
  /** Android browser without a prompt (Firefox, Samsung Internet, or Chrome before it's ready): menu steps. */
  | 'android-manual'
  /** iPhone or iPad in Safari: Share → Add to Home Screen. */
  | 'ios-safari'
  /** iPhone or iPad in Chrome, Edge, Firefox…: also possible, through that browser's share menu. */
  | 'ios-other'
  /** A page shown inside another app (an email client, Facebook, Instagram, TikTok…) can't install: open it in the real browser. */
  | 'in-app'
  /** Desktop browser without a prompt. */
  | 'none';

export interface InstallEnv {
  userAgent: string;
  platform?: string;
  maxTouchPoints?: number;
  standalone: boolean;
  native: boolean;
  /** The browser gave us an install prompt. */
  hasPrompt: boolean;
}

const IN_APP =
  /FBAN|FBAV|FB_IAB|Instagram|Line\/|MicroMessenger|Snapchat|TikTok|musical_ly|Twitter|LinkedInApp|Pinterest|GSA\/|Outlook|YahooMobile|WhatsApp|Slack|Discord|Telegram/i;

/** iPhones, iPods and iPads (iPadOS pretends to be a Mac, so it's told apart by its touch screen). */
export function isIos(env: Pick<InstallEnv, 'userAgent' | 'platform' | 'maxTouchPoints'>): boolean {
  if (/iPhone|iPad|iPod/i.test(env.userAgent)) return true;
  return (env.platform === 'MacIntel' || /Macintosh/.test(env.userAgent)) && (env.maxTouchPoints ?? 0) > 1;
}

/** What this device and browser can do about installing. Pure, so it's easy to test. */
export function detectInstallContext(env: InstallEnv): InstallContext {
  if (env.native || env.standalone) return 'installed';
  const ua = env.userAgent;
  if (IN_APP.test(ua)) return 'in-app';
  if (isIos(env)) {
    // Chrome, Edge, Firefox and Opera on iOS say so in their user agent; Safari doesn't.
    if (/CriOS|EdgiOS|FxiOS|OPiOS|DuckDuckGo/i.test(ua)) return 'ios-other';
    // Real Safari ends its user agent with "Safari/…". A page shown inside another app
    // (an email client, a chat app) is a web view, which leaves that off and can't install.
    return /Safari\//.test(ua) ? 'ios-safari' : 'in-app';
  }
  // An Android web view (inside another app) says "wv".
  if (/; wv\)/.test(ua)) return 'in-app';
  if (env.hasPrompt) return 'prompt';
  if (/Android/i.test(ua)) return 'android-manual';
  return 'none';
}

// ---- Asking again: not too often, and not forever ----

export const REASK_AFTER_DAYS = 14;
export const MAX_DISMISSALS = 3;
const STORAGE_KEY = 'tradeprep-install-banner';

export interface Dismissals {
  count: number;
  /** ISO time of the last "Not now". */
  last: string | null;
}

/** Whether the banner may show: never after 3 dismissals, and not for 2 weeks after the last one. */
export function mayAsk(d: Dismissals, now = Date.now()): boolean {
  if (d.count >= MAX_DISMISSALS) return false;
  if (!d.last) return true;
  return now - Date.parse(d.last) >= REASK_AFTER_DAYS * 24 * 3600 * 1000;
}

function readDismissals(): Dismissals {
  try {
    const v = JSON.parse(localStorage.getItem(STORAGE_KEY) ?? 'null') as Partial<Dismissals> | null;
    return { count: Number(v?.count) || 0, last: typeof v?.last === 'string' ? v.last : null };
  } catch {
    return { count: 0, last: null };
  }
}

function writeDismissals(d: Dismissals) {
  try {
    localStorage.setItem(STORAGE_KEY, JSON.stringify(d));
  } catch {
    /* private mode: it just asks again next visit */
  }
}

// ---- The browser's install prompt ----

interface BeforeInstallPromptEvent extends Event {
  prompt: () => Promise<void>;
  userChoice: Promise<{ outcome: 'accepted' | 'dismissed' }>;
}

let deferred: BeforeInstallPromptEvent | null = null;
let installedNow = false;
let version = 0;
const listeners = new Set<() => void>();
const changed = () => {
  version += 1;
  listeners.forEach((l) => l());
};

let started = false;
/** Start listening for the browser's install prompt. Call once, as early as possible (it can fire before the app has loaded). */
export function startInstallListener() {
  if (started || typeof window === 'undefined') return;
  started = true;
  window.addEventListener('beforeinstallprompt', (e) => {
    e.preventDefault(); // we offer it ourselves, at a good moment
    deferred = e as BeforeInstallPromptEvent;
    changed();
  });
  window.addEventListener('appinstalled', () => {
    deferred = null;
    installedNow = true;
    changed();
  });
}

const currentEnv = (): InstallEnv => ({
  userAgent: navigator.userAgent,
  platform: navigator.platform,
  maxTouchPoints: navigator.maxTouchPoints,
  standalone: installedNow || isStandalonePwa(),
  native: isNative(),
  hasPrompt: !!deferred,
});

/** Whether this device is an iPhone or iPad (for wording the steps). */
export const isIosDevice = (): boolean =>
  typeof navigator !== 'undefined' && isIos({ userAgent: navigator.userAgent, platform: navigator.platform, maxTouchPoints: navigator.maxTouchPoints });

export type InstallOutcome = 'accepted' | 'dismissed' | 'instructions';

export interface InstallState {
  context: InstallContext;
  /** Something can be offered here (the install is one tap, or there are steps to show). */
  available: boolean;
  /** The banner may show now: available, and the learner hasn't waved it away too often. */
  mayShowBanner: boolean;
  /** One tap installs. Otherwise `showInstructions` is the way. */
  oneTap: boolean;
  /** The browser's own install dialog. Resolves 'instructions' when there's no prompt (show the steps instead). */
  install: () => Promise<InstallOutcome>;
  /** "Not now": counts towards asking less often. */
  dismiss: () => void;
}

let cached: { v: number; dismissed: Dismissals; state: InstallState } | null = null;

function snapshot(): InstallState {
  const dismissed = readDismissals();
  if (cached && cached.v === version && cached.dismissed.count === dismissed.count && cached.dismissed.last === dismissed.last) return cached.state;
  const context = detectInstallContext(currentEnv());
  const available = context !== 'installed' && context !== 'none';
  const state: InstallState = {
    context,
    available,
    mayShowBanner: available && mayAsk(dismissed),
    oneTap: context === 'prompt',
    async install() {
      const event = deferred;
      if (!event) return 'instructions';
      deferred = null; // an install prompt can only be used once
      changed();
      await event.prompt();
      const { outcome } = await event.userChoice;
      if (outcome === 'dismissed') state.dismiss();
      else {
        installedNow = true;
        changed();
      }
      return outcome;
    },
    dismiss() {
      const d = readDismissals();
      writeDismissals({ count: d.count + 1, last: new Date().toISOString() });
      changed();
    },
  };
  cached = { v: version, dismissed, state };
  return state;
}

function subscribe(listener: () => void) {
  listeners.add(listener);
  return () => void listeners.delete(listener);
}

/** What the app can offer for installing, kept up to date as the browser's prompt arrives. */
export function useInstall(): InstallState {
  return useSyncExternalStore(subscribe, snapshot, snapshot);
}

/** For tests. */
export function resetInstallForTests() {
  deferred = null;
  installedNow = false;
  cached = null;
  version += 1;
}
